import type en from "../en/settings";
import type { Translation } from "../../types";

const settings: Translation<typeof en> = {
  workspace: {
    title: "Paramètres",
    intro:
      "Les chapitres et la discussion utilisent votre propre clé de modèle. Nous relayons les appels et ne vous facturons jamais l’inférence.",
    provider: {
      title: "Fournisseur de modèles",
      anthropicHint: "Une courte liste sélectionnée",
      openrouterHint: "Des centaines de modèles, prix en direct",
    },
    apiKey: {
      label: "Clé API",
      ends: "se termine par {{hint}}",
      remove: "Supprimer la clé",
      replacePlaceholder: "Saisissez une nouvelle clé pour la remplacer",
      help: "Vérifiée auprès de {{provider}} avant d’être enregistrée, puis chiffrée. Elle n’est plus jamais renvoyée au navigateur.",
    },
    model: {
      label: "Modèle",
      needsKey: "Ajoutez une clé ci-dessus pour parcourir les modèles de {{provider}}.",
      filter_one: "Filtrer {{count}} modèle…",
      filter_other: "Filtrer {{count}} modèles…",
      free: "Gratuit",
      context: "contexte de {{size}} k",
      pricing: "Prix en USD par million de jetons en entrée / en sortie.",
    },
    removeConfirm: {
      question:
        "Supprimer cette clé ? Elle est effacée de notre base de données, et les chapitres, les résumés et la discussion cessent de fonctionner jusqu’à ce que vous en ajoutiez une autre.",
      kept: "Les résumés et chapitres déjà générés sont conservés — vous les avez payés, ils restent.",
      keep: "La conserver",
      failed: "Impossible de supprimer cette clé.",
    },
    saveFailed: "Impossible d’enregistrer cette clé.",
    saveCredential: "Enregistrer la clé",
    savedVerified: "Enregistrée et vérifiée",
    general: {
      title: "Espace de travail",
      botName: "Nom de l’assistant",
      botNamePlaceholder: "Assistant de prise de notes",
      botNameHelp:
        "Chaque participant le voit dans la liste de la réunion. Ce produit n’enregistre jamais personne sans un avertissement évident : choisissez un nom reconnaissable.",
      retention: "Supprimer les enregistrements après",
      retentionPlaceholder: "Jamais",
      days: "jours",
      retentionInvalid: "Saisissez un nombre entier de jours, ou laissez le champ vide.",
      retentionHelp:
        "Laissez vide pour conserver les enregistrements indéfiniment. La vidéo et sa transcription sont supprimées définitivement — impossible d’annuler.",
      saved: "Enregistré",
    },
  },
  account: {
    title: "Votre compte",
    intro: "Ces paramètres vous suivent dans tous vos espaces de travail.",
    signedInAs: "Connecté en tant que",
    platformOperator: "Opérateur de la plateforme",
    languageHelp: "Utilisée pour l’interface et les e-mails que nous vous envoyons.",
    password: {
      title: "Mot de passe",
      body: "Pour le changer, nous envoyons un lien à votre adresse e-mail. C’est le même parcours qu’en cas d’oubli : un mot de passe ne peut être défini que d’une seule façon.",
      sendReset: "Envoyer un lien de réinitialisation",
    },
    twoFactor: {
      title: "Authentification à deux facteurs",
      body: "Un code depuis votre téléphone en plus de votre mot de passe. Les enregistrements de vos réunions sont aussi sensibles que les réunions elles-mêmes.",
      turnOn: "L’activer",
      turnOff: "La désactiver",
      confirmPassword: "Confirmez votre mot de passe",
      enable: "Activer l’authentification à deux facteurs",
      disable: "Désactiver l’authentification à deux facteurs",
      disabled: "L’authentification à deux facteurs est désactivée.",
      savedTitle: "Authentification à deux facteurs activée — enregistrez ceci maintenant",
      savedBody:
        "Ajoutez cette clé à votre application d’authentification et conservez les codes de secours en lieu sûr. C’est la seule fois où ils sont affichés.",
      setupKey: "Clé de configuration",
      backupCodes: "Codes de secours",
      savedThem: "Je les ai enregistrés",
    },
  },
  members: {
    title: "Personnes",
    intro:
      "Les rôles ne s’appliquent qu’à cet espace de travail — la même personne peut avoir un autre rôle ailleurs.",
    rejected: "Cette modification a été refusée.",
    suspended: "Suspendu",
    removeTitle: "Retirer de l’espace de travail",
    removeAria: "Retirer {{email}}",
    removeOwner: {
      title: "Retirer un propriétaire de cet espace de travail ?",
      body: "{{name}} est propriétaire. Le retirer lui enlève en une seule fois la propriété et l’accès, et seul un autre propriétaire pourra le réintégrer. Ses enregistrements sont conservés.",
      confirm: "Oui, retirer ce propriétaire",
    },
    removeMember:
      "Retirer <strong>{{email}}</strong> de cet espace de travail ? L’accès est perdu immédiatement. Ses enregistrements sont conservés.",
    promote: {
      title: "Faire de {{name}} un propriétaire ?",
      body: "Les propriétaires contrôlent entièrement cet espace de travail, y compris les autres administrateurs, les autres propriétaires, et vous-même.",
      confirm: "Oui, en faire un propriétaire",
    },
    demote: {
      title: "Retirer la propriété à {{name}} ?",
      body: "Cette personne devient {{role}} et perd le contrôle des personnes et des paramètres. L’espace de travail doit toujours garder au moins un propriétaire.",
      confirm: "Oui, retirer la propriété",
    },
    roleInSentence: {
      user: "membre",
      creator: "créateur",
      admin: "administrateur",
      super_admin: "propriétaire",
    },
    rolesTitle: "Ce que signifient les rôles",
    roleDescription: {
      user: "Lecture seule : regarder les enregistrements, rechercher et utiliser la discussion",
      creator: "Tout ce qui précède, plus créer et monter — enregistrements, Studio, assistants",
      admin: "Tout ce qui précède, plus inviter des personnes et gérer les créateurs et les membres",
      super_admin: "Contrôle total, y compris sur les administrateurs et les autres propriétaires",
    },
    invite: {
      title: "Inviter quelqu’un",
      body: "La personne reçoit un e-mail avec un lien pour définir un mot de passe et rejoindre cet espace de travail.",
      email: "E-mail",
      emailPlaceholder: "collegue@entreprise.com",
      role: "Rôle",
      send: "Envoyer l’invitation",
      failed: "Impossible d’envoyer cette invitation.",
      resent: "Invitation renvoyée à {{email}}",
      sent: "{{email}} a été invité",
      waiting: "En attente d’acceptation",
      roleExpired: "{{role}} · expirée",
      expired: "Expirée",
      revoke: "Révoquer",
    },
  },
};

export default settings;
